/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { ICartLinePriceEditorResources, PriceComponent } from '@msdyn365-commerce/components';
import { IComponentProps, IGridSettings, IImageSettings, Image } from '@msdyn365-commerce/core';
import { ICartState } from '@msdyn365-commerce/global-state';
import { ChannelDeliveryOptionConfiguration, ProductDimensionType, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    CartLine,
    CommerceProperty,
    DiscountLine,
    DiscountLineType,
    SalesLine
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { ArrayExtensions, getFallbackImageUrl } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, IncrementalQuantity, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classNames from 'classnames';
import * as React from 'react';
import { CommercePropertyValueEnum } from '../../../../modules/allowable-freight/utils/CommercePropertyValueEnum';
import { WarehouseInventoryType } from './inventory-breakdown.component';
import { computeWarehouseStateForLine, getSupersessionPreview, WarehouseState } from '../../../../utils/alternateWarehouseHelper';
import { getFallbackImageSrc, getManufacturerCode } from '../../../../utils/ImageFallbackUtil';
import { SupersessionBreakdownComponent, SupersessionPreview } from './supersession-breakdown.component';

/**
 * ICartlineResourceString: Interface for specifying the
 * resource strings that the component needs.
 */
export interface ICartlineResourceString {
    /** Display string for discount label. */
    discountStringText: string;

    /** String for size key.  */
    sizeString: string;

    /** String for color key.  */
    colorString: string;

    /** String associated with the configuration product dimension.  */
    configString: string;

    /** String for style key.  */
    styleString: string;

    /** String for amount key.  */
    amountString?: string;

    /** Display string for quantity label.  */
    quantityDisplayString: string;

    /** Display string for quantity label.  */
    inputQuantityAriaLabel: string;

    /** Aria label for the decrement button in quantity component.  */
    decrementButtonAriaLabel: string;

    /** Aria label for the increment button in quantity component.  */
    incrementButtonAriaLabel: string;

    /** Original text screenreader. */
    originalPriceText: string;

    /** Current text screenreader.  */
    currentPriceText: string;

    /** Shipping Charges Text.  */
    shippingChargesText: string;

    /** Price Editor Charges Text.  */
    priceEditorResources?: ICartLinePriceEditorResources;
}

/**
 * Represents cart line data interface.
 */
interface ICartLineData {
    cartLine: CartLine | SalesLine;
    cartState?: ICartState;
    product?: SimpleProduct;
}

/**
 * ICartLineProps: The props required to render cartLineitem.
 */
export interface ICartLineProps extends IComponentProps<ICartLineData> {
    /** The flag to change the quantity component from interactible to static.  */
    disableQuantityInteractivity?: boolean;

    /** The primary image url.  */
    primaryImageUrl?: string;

    /** The product url.  */
    productUrl?: string;

    /** GridSettings for the product image in cartLine.  */
    gridSettings: IGridSettings;

    /** ImageSettings for the product image in cartLine.  */
    imageSettings: IImageSettings;

    /** Boolean flag to indicate if the item is out of stock.  */
    isOutOfStock?: boolean;

    /** Flag to make quantity section editable.  */
    isQuantityEditable?: boolean;

    /** Max quantity for line item.  */
    maxQuantity?: number;

    /** Current quantity for line item.  */
    currentQuantity?: number;

    /** Resource string for the component.  */
    resources: ICartlineResourceString;

    /** SalesLine flag.  */
    isSalesLine?: boolean;

    /** Error message to show in place of quantity. */
    errorMessage?: string;

    /** Inventory information label.  */
    inventoryInformationLabel?: string;

    /** Inventory information class name.  */
    inventoryLabelClassName?: string;

    /** Flag to show/hide shipping charges for line items.  */
    showShippingChargesForLineItems?: boolean;

    /** Boolean flag to indicate if cart state status is ready.  */
    isCartStateReady?: boolean;

    /** Chanel Delivery Option configuration is from api.  */
    channelDeliveryOptionConfig?: ChannelDeliveryOptionConfiguration;

    /** The telemetry content. */
    telemetryContent?: ITelemetryContent;

    /** Quantity onChange callback.  */
    quantityOnChange?(cartLine: CartLine, newQuantity: number): boolean;

    /** Expected shipping warehouse */
    warehouse: WarehouseInventoryType | undefined;

    /** Whether the displayed cartline is on cartpage or mini cart */
    isCartView: string;

    /** Whether the current warehouse is altered or not */
    isAlternateWarehouse: boolean;

    /** Whether alternate warehouse selection should be disabled due to applied coupon codes. */
    isAlternateWarehouseSelectionDisabled: boolean;

    /** Number of warehouses for availabilities */
    numOfWarehouse: number | undefined;

    /** Determines whether the alternate warehouse feature should be displayed for the user. */
    shouldDisplayAlternateWarehouseFeature: boolean;

    /** Primary warehouse */
    primaryWarehouse: WarehouseInventoryType | undefined;

    /** Whether warehouse has changed to primary warehouse due to insufficient stock */
    hasWarehouseChanged: boolean | undefined;

    /** Extension properties containing warehouse data */
    productExtensionProperties: CommerceProperty[] | undefined;

    /** Whether to enforce primary warehouse availability before selecting an alternate warehouse. */
    // enforcePrimaryWarehouseAvailability: boolean;

    warehouses: WarehouseInventoryType[] | undefined;

    /** Function to set product for availability modal */
    selectProductForAvailability: (cartlineId: string) => void;

    primaryWarehouseChangedLabel: string;
    alternateWarehouseChangedLabel: string;
    primaryWarehouseShippingLabel: string;
    alternateWarehouseSelectionNotAvailableMessage: string;
    singleSourcingWarningLabel: string;
    warehouseAvailabilityLinkLabel: string;
    stockLabel: string;
    multipleWarehouseShippingLabel: string;
    qtyAvailableOnBackOrderLabel: string;
    computedState: WarehouseState;
    substitutionLabel: string;
    priceLabel: string;
    substitutionItemHeaderLabel: string;
    substitutionBackorderItemHeaderLabel: string;
    substitutionQtyLabel: string;
    substitutionWarehouseLabel: string;
    /** Shipping state from checkout - used for Prop 65 warning */
    checkoutShippingState?: string;
}

/**
 * Represents dimensions strings.
 */
interface IDimensionStrings {
    /** String for size key.  */
    sizeString: string;

    /** String for color key.  */
    colorString: string;

    /** String associated with the configuration product dimension.  */
    configString: string;

    /** String for style key.  */
    styleString: string;

    /** String for amount key.  */
    amountString?: string;
}

const cartLineItemFunctions = {
    /**
     * Render product dimensions.
     * @param product - Product data.
     * @param dimensionStrings - Dimension strings interface.
     * @returns JSX Element.
     */
    renderProductDimensions: (product: SimpleProduct, dimensionStrings: IDimensionStrings) => {
        if (!product.Dimensions) {
            return [];
        }

        return product.Dimensions.map(productDimension => {
            if (productDimension.DimensionTypeValue === ProductDimensionType.Color) {
                return (
                    <div key={`${product.RecordId}ProductDimensions1`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-color'>
                            {dimensionStrings.colorString}
                            {': '}
                            <span className='name'>{productDimension.DimensionValue?.Value}</span>
                        </span>
                    </div>
                );
            }

            if (productDimension.DimensionTypeValue === ProductDimensionType.Configuration) {
                return (
                    <div key={`${product.RecordId}ProductDimensions2`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-configuration'>
                            {dimensionStrings.configString}
                            {': '}
                            <span className='name'>{productDimension.DimensionValue?.Value}</span>
                        </span>
                    </div>
                );
            }

            if (productDimension.DimensionTypeValue === ProductDimensionType.Size) {
                return (
                    <div key={`${product.RecordId}ProductDimensions3`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-size'>
                            {dimensionStrings.sizeString}
                            {': '}
                            <span className='name'>{productDimension.DimensionValue?.Value}</span>
                        </span>
                    </div>
                );
            }

            if (productDimension.DimensionTypeValue === ProductDimensionType.Style) {
                return (
                    <div key={`${product.RecordId}ProductDimensions4`} className='msc-cart-line__product-variant-item'>
                        <span className='msc-cart-line__product-variant-style'>
                            {product.IsGiftCard ? dimensionStrings.amountString : dimensionStrings.styleString}
                            {': '}
                            <span className='name'>{productDimension.DimensionValue?.Value}</span>
                        </span>
                    </div>
                );
            }
            return null;
        });
    },
    //
    //
    /**
     * Gets the react node for product unit of price display.
     * @param props - The cart line props.
     * @returns The node representing markup for unit of measure component.
     */
    renderProductUnitPrice: (props: ICartLineProps) => {
        if (!props.data.cartLine.Price) {
            return null;
        }
        const zeroQuantity: number = 0;
        return (
            <div className='msc-cart-line__product-unit-price'>
                <span>
                    <PriceComponent
                        data={
                            props.data.cartLine.Quantity && props.data.cartLine.Quantity > zeroQuantity
                                ? {
                                      price: {
                                          CustomerContextualPrice: props.data.cartLine.Price
                                      }
                                  }
                                : {
                                      price: {
                                          CustomerContextualPrice: props.data.cartLine.Price
                                      }
                                  }
                        }
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                    />
                </span>
            </div>
        );
    },

    /**
     * Render discount lines.
     * @param props - The cart line props.
     * @returns JSX element.
     */
    renderDiscountLines: (props: ICartLineProps) => {
        if (!props.data.cartLine.DiscountLines || !ArrayExtensions.hasElements(props.data.cartLine.DiscountLines)) {
            return null;
        }

        /**
         * Get the updated discount percentage.
         * @param discountLine - The discount line.
         * @param cartLine - The cart line.
         * @returns The updated discount percentage.
         */
        const discountPercent = (discountLine: DiscountLine, cartLine: CartLine): number => {
            const numberZero = 0;
            const decimalPrecision = 2;
            const fullPercentage = 100;
            let updatedPercent: number = discountLine.Percentage ?? numberZero;

            if (
                (discountLine.Percentage === numberZero ||
                    (discountLine.DiscountLineTypeValue && discountLine.DiscountLineTypeValue === DiscountLineType.TenderTypeDiscount)) &&
                cartLine.Price &&
                cartLine.Price > numberZero &&
                cartLine.Quantity &&
                cartLine.Quantity !== numberZero
            ) {
                const effectiveAmount = discountLine.EffectiveAmount ?? numberZero;
                updatedPercent = Number(
                    ((effectiveAmount / (cartLine.Price * cartLine.Quantity)) * fullPercentage).toFixed(decimalPrecision)
                );
            }

            return updatedPercent;
        };

        return props.data.cartLine.DiscountLines.map((discountLine, index) => {
            return (
                <div key={discountLine.OfferId ?? index} className='msc-cart-line__product-discount'>
                    <span>{discountLine.OfferName ? discountLine.OfferName : ''}</span>
                    <span className='msc-cart-line-item-product-discount-price'>
                        <PriceComponent
                            data={
                                props.isSalesLine
                                    ? {
                                          price: {
                                              // @ts-expect-error -- Auto suppressed.
                                              CustomerContextualPrice: props.data.cartLine.PeriodicDiscount
                                          }
                                      }
                                    : {
                                          price: {
                                              CustomerContextualPrice: discountLine.EffectiveAmount,
                                              BasePrice: discountLine.EffectiveAmount
                                          }
                                      }
                            }
                            context={props.context}
                            id={props.id}
                            typeName={props.typeName}
                        />
                    </span>
                    <span className='msc-cart-line__product-discount-percentage'>
                        {` (${discountLine.Percentage !== undefined ? discountPercent(discountLine, props.data.cartLine) : ''}%)`}
                    </span>
                </div>
            );
        });
    },

    /**
     * Render inventory label.
     * @param props - The cart line props.
     * @returns JSX element.
     */
    renderInventoryLabel: (props: ICartLineProps, supersessionPreview: SupersessionPreview | undefined) => {
        const {
            context: {
                request: {
                    app: {
                        routes: {
                            cart: { destinationUrl }
                        }
                    },
                    canonicalUrl
                }
            }
        } = props;
        const inventoryCssName = props.inventoryLabelClassName
            ? `msc-cart-line__product-inventory-label ${props.inventoryLabelClassName}`
            : 'msc-cart-line__product-inventory-label';
        const currPage = canonicalUrl?.substring(canonicalUrl.lastIndexOf('/'));
        const isCartPage = currPage === destinationUrl;
        const handleLinkClick = (e: React.MouseEvent) => {
            e.preventDefault();
            props.selectProductForAvailability(props.data.cartLine.LineId!);
        };
        const isInvoiceLine = props.data.cartLine.IsInvoiceLine;

        const computedState =
            props.computedState ??
            computeWarehouseStateForLine(
                props.data.cartLine,
                props.warehouses || undefined,
                props.productExtensionProperties,
                props.context.app.config,
                props.stockLabel,
                props.isAlternateWarehouseSelectionDisabled
            );

        const {
            hasWarehouseChanged,
            getWarehouseNameFromMappings,
            isAlternateWarehouse,
            shouldDisplayAutoSourcedMessage,
            isWarehouseOverride,
            totalStock,
            inStockLabel,
            shipmentInfo
        } = computedState;

        const warehouseName = getWarehouseNameFromMappings(props.warehouse?.WarehouseId) ?? props.warehouse?.Name;
        const allocCount = supersessionPreview?.Allocations?.length ?? 0;
        const hasSupersessionAllocations = allocCount > 0;

        const backorderBits = !hasSupersessionAllocations ? (
            <>
                {shipmentInfo.backorderQty} {props.qtyAvailableOnBackOrderLabel}
            </>
        ) : null;

        function renderWarehouseMessage() {
            // 1) If the selected warehouse is not primary warehouse
            if (props.isAlternateWarehouse) {
                // User selected warehouse was allowable warehouse (no freight charge)
                if (!isAlternateWarehouse && !isWarehouseOverride) {
                    return (
                        <span>
                            {props.primaryWarehouseChangedLabel}
                            {warehouseName}
                        </span>
                    );
                }
                // Warehouse auto sourced to a secondary warehouse due to insufficient QTY in primary warehouse
                if (isWarehouseOverride) {
                    return (
                        <>
                            {props.primaryWarehouseShippingLabel}
                            {warehouseName}
                        </>
                    );
                }
                // User selected warehouse was an alternate warehouse
                return (
                    <span>
                        {props.alternateWarehouseChangedLabel}
                        {warehouseName}
                    </span>
                );
            }

            // 2) Partial QTY can be fulfilled by a single warehouse and rest on backorder
            if (shipmentInfo.type === 'PARTIAL_SINGLE') {
                return (
                    <>
                        {props.primaryWarehouseShippingLabel} {getWarehouseNameFromMappings(shipmentInfo.warehouseId) ?? warehouseName}.{' '}
                        {backorderBits}
                    </>
                );
            }

            // 3) Partial QTY can be fulfilled by multiple warehouses and rest on backorder
            if (shipmentInfo.type === 'PARTIAL_MULTI') {
                return (
                    <>
                        {props.multipleWarehouseShippingLabel} {backorderBits}
                    </>
                );
            }

            if (shipmentInfo.type === 'FULL_MULTI') {
                return <>{props.multipleWarehouseShippingLabel}</>;
            }

            // 4) Default: full single‑warehouse fulfillment
            return (
                <>
                    {props.primaryWarehouseShippingLabel} {warehouseName}
                </>
            );
        }

        return !isInvoiceLine ? (
            <div className='msc-cart-line__product__alternate-warehouse'>
                <p className={inventoryCssName}>
                    {!props.isAlternateWarehouse && (totalStock === 0 ? props.inventoryInformationLabel : inStockLabel)}
                </p>
                {props.shouldDisplayAlternateWarehouseFeature && props.isCartView && props.numOfWarehouse && (
                    <>
                        <p className='warehouse-info'>
                            {/* Renders the expected to ship from message based on various conditions */}
                            {renderWarehouseMessage()}

                            {/* Renders the freight charge message for alternate warehouses  */}
                            {props.isAlternateWarehouse && isAlternateWarehouse && !isWarehouseOverride && (
                                <p className='freight-charge-message'>
                                    <Msdyn365.RichTextComponent text={props.context.app.config.freightChargeMessage} />
                                </p>
                            )}

                            {/* Renders the warehouse has changed message due to insufficient qty*/}
                            {hasWarehouseChanged && !props.isAlternateWarehouseSelectionDisabled && !props.isAlternateWarehouse && (
                                <p className='warehouse-changed-message'>
                                    <Msdyn365.RichTextComponent text={props.context.app.config.warehouseChangedLabel} />
                                </p>
                            )}

                            {/* Renders auto sourced message if user selected primary warehouse and it didn't have enough stock but other warehouse did */}
                            {isWarehouseOverride && shouldDisplayAutoSourcedMessage && (
                                <p className='auto-sourced-message'>
                                    <Msdyn365.RichTextComponent
                                        text={
                                            props.context.app.config.warehouseAutoSourcedLabel ||
                                            'Item auto sourced to another warehouse. Item not available in the selected warehouse.'
                                        }
                                    />
                                </p>
                            )}
                        </p>

                        {isCartPage &&
                            (props.isAlternateWarehouseSelectionDisabled ? (
                                <p className='promotion-message'>{props.alternateWarehouseSelectionNotAvailableMessage}</p>
                            ) : props.numOfWarehouse && props.numOfWarehouse === 1 ? (
                                <p className='no-warehouse'>{props.singleSourcingWarningLabel}</p>
                            ) : (
                                // !isPrimaryWarehouseEnforced &&
                                <a href='javascript:void(0)' className='gdn-inventory-link' onClick={handleLinkClick}>
                                    {props.warehouseAvailabilityLinkLabel}
                                </a>
                            ))}
                    </>
                )}
            </div>
        ) : null;
    },

    /**
     * Render shipping label.
     * @param props - The cart line props.
     * @returns JSX element.
     */
    renderShippingLabel: (props: ICartLineProps) => {
        const pickupDeliveryModeCode = props.context.request.channel?.PickupDeliveryModeCode;
        const channelDeliveryOptionConfig = props.channelDeliveryOptionConfig;
        const cartline = props.data.cartLine;
        let hasShippingMethod = false;
        if (channelDeliveryOptionConfig !== undefined) {
            hasShippingMethod = !!(
                cartline.DeliveryMode &&
                channelDeliveryOptionConfig.PickupDeliveryModeCodes?.some(deliveryMode => deliveryMode !== cartline.DeliveryMode)
            );
        } else {
            hasShippingMethod = !!(cartline.DeliveryMode && cartline.DeliveryMode !== pickupDeliveryModeCode);
        }
        if (!hasShippingMethod) {
            return undefined;
        }

        const shippingChargeLines = (cartline.ChargeLines ?? []).filter(chargeLine => chargeLine.IsShipping);

        if (!ArrayExtensions.hasElements(shippingChargeLines)) {
            return undefined;
        }

        const defaultCharge: number = 0;
        const freightFee = shippingChargeLines.reduce((chargeTotal, chargeLine) => {
            return chargeTotal + (chargeLine.CalculatedAmount ?? defaultCharge);
        }, defaultCharge);

        const priceComponent = (
            <PriceComponent
                data={{
                    price: {
                        CustomerContextualPrice: freightFee
                    }
                }}
                freePriceText='Free'
                context={props.context}
                id={props.id}
                typeName={props.typeName}
                className='msc-cart-line__freight-actual'
            />
        );
        return (
            <>
                <label className='msc-cart-line__freight-label'>{`${props.resources.shippingChargesText}:`}</label>
                <span className='shipping-value'>{priceComponent}</span>
            </>
        );
    },

    /**
     * Render other charges.
     * @param props - The cart line props.
     * @returns JSX element.
     */
    renderOtherCharges: (props: ICartLineProps) => {
        const cartline = props.data.cartLine;
        const otherCharges = cartline.ChargeLines?.filter(chargeline => !chargeline.IsShipping);

        return (
            otherCharges?.map((otherCharge, index) => {
                const itemIndex = index;
                return otherCharge.CalculatedAmount ? (
                    <div className='msc-cart-line__other-charges' key={itemIndex}>
                        <label className='msc-cart-line__other-charges-label'>{`${String(otherCharge.Description)}:`}</label>
                        <span className='other-charge-value'>
                            <PriceComponent
                                data={{
                                    price: {
                                        CustomerContextualPrice: otherCharge.CalculatedAmount
                                    }
                                }}
                                context={props.context}
                                id={props.id}
                                typeName={props.typeName}
                                className='msc-cart-line__other-charges-actual'
                            />
                        </span>
                    </div>
                ) : (
                    ''
                );
            }) ?? undefined
        );
    }
};

/**
 * Render cartline component.
 * @param props - The cart line props.
 * @returns JSX element.
 */
const CartLine: React.FC<ICartLineProps> = (props: ICartLineProps) => {
    // Use checkout-level shipping state if available, otherwise fall back to line-level
    const shippingState = props.checkoutShippingState || props.data.cartLine.ShippingAddress?.State;

    const { isSalesLine, productUrl, resources } = props;
    const { product, cartLine } = props.data;
    const destructDimensionStrings = {
        sizeString: resources.sizeString,
        colorString: resources.colorString,
        styleString: resources.styleString,
        configString: resources.configString,
        amountString: resources.amountString
    };

    const IsInvoiceLine = cartLine.IsInvoiceLine;

    const fallbackImageUrl = product && getFallbackImageUrl(product.ItemId, props.context.actionContext.requestContext.apiSettings);
    const productDimensions = product && cartLineItemFunctions.renderProductDimensions(product, destructDimensionStrings);
    const renderProductUnitPrice = cartLineItemFunctions.renderProductUnitPrice(props);
    const imageSettings = props.imageSettings;
    if (imageSettings) {
        imageSettings.cropFocalRegion = true;
    }
    const supersessionPreview = getSupersessionPreview(props.data.cartLine);

    const renderDiscountLines = cartLineItemFunctions.renderDiscountLines(props);
    const renderInventoryLabel = cartLineItemFunctions.renderInventoryLabel(props, supersessionPreview);
    const renderShippingLabel = cartLineItemFunctions.renderShippingLabel(props);
    const renderOtherCharges = cartLineItemFunctions.renderOtherCharges(props);
    const payLoad = getPayloadObject('click', props.telemetryContent!, '', product?.RecordId.toString());
    const productAttribute = getTelemetryAttributes(props.telemetryContent!, payLoad);
    const productName = product?.Name ?? cartLine.Description;
    const imgeClassName = props.data.cartLine.IsInvoiceLine ? 'msc-cart-line__invoice-image' : 'msc-cart-line__product-image';
    const cartLineContentClassName = props.data.cartLine.IsInvoiceLine ? 'msc-cart-line__invoice-content' : 'msc-cart-line__content';

    /**
     * Render other charges.
     * @param newValue - The cart line props.
     * @returns Boolean.
     */
    const onChange = (newValue: number): boolean => {
        if (props.quantityOnChange) {
            return props.quantityOnChange(props.data.cartLine, newValue);
        }
        return true;
    };

    /**
     * Generate error message.
     * @returns JSX element.
     */
    const generateErrorMessage = (): JSX.Element | null => {
        if (props.errorMessage) {
            return (
                <div className='msc-alert__header'>
                    <span className='msi-exclamation-triangle' />
                    <span>{props.errorMessage}</span>
                </div>
            );
        }

        return null;
    };

    /**
     * Gets the react node for product unit of measure display.
     * @returns The node representing markup for unit of measure component.
     */
    const renderUnitOfMeasure = () => {
        if (props.data.cartLine.IsInvoiceLine) {
            return null;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Do not need type check for appsettings
        if (!props.context.app.config.unitOfMeasureDisplayType || props.context.app.config.unitOfMeasureDisplayType === 'none') {
            return undefined;
        }

        if (!product || !product.DefaultUnitOfMeasure) {
            return undefined;
        }

        return (
            <div className='msc-cart-line__product-unit-of-measure'>
                <span>{product.DefaultUnitOfMeasure}</span>
            </div>
        );
    };

    const getExtensionPropertyValueByKey = (
        properties: CommerceProperty[] | undefined,
        key: string,
        valueType: CommercePropertyValueEnum
    ) => {
        const property = properties?.find(p => p.Key === key)?.Value;

        // eslint-disable-next-line security/detect-object-injection
        const val = property ? property[valueType] : null;
        return val;
    };

    /**
     * Render Prop 65 warning for California shipments.
     * @returns JSX element.
     */
    const renderProp65Warning = (): JSX.Element | null => {
        // Only show warning if shipping to California and toggle is enabled
        if (shippingState !== 'CA' || !props.context.app.config.prop65toggle) {
            return null;
        }

        return (
            <div className='msc-cart-line__prop65-warning-container'>
                <div className='msc-cart-line__svg_warning_Icon'>
                    <svg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
                        {/* Yellow triangle with hollow center */}
                        <polygon points='12,2 22,20 2,20' fill='white' stroke='#FFD700' strokeWidth='2' strokeLinejoin='miter' />
                        {/* Solid exclamation bar */}
                        <rect x='11' y='8' width='2' height='6' fill='#FFD700' />
                        {/* Exclamation dot */}
                        <rect x='11' y='16' width='2' height='2' fill='#FFD700' />
                    </svg>
                </div>

                <div className='msc-cart-line__prop65-warning-message_container'>
                    <Msdyn365.RichTextComponent
                        className='msc-cart-line__prop65-warning-message'
                        text={props.context.app.config.prop65WarningMessage}
                    />
                </div>
            </div>
        );
    };

    /**
     * Generate quantity and price component.
     * @returns JSX element.
     */
    const generateQuantityAndPrice = (): JSX.Element[] | null => {
        const nodes = [];
        const defaultMaxQuantity: number = 10;
        const singleQuantity: number = 1;
        const unitOfMeasure = renderUnitOfMeasure();
        const courseId = getExtensionPropertyValueByKey(
            props.data.cartLine.ExtensionProperties,
            'CourseId',
            CommercePropertyValueEnum.string
        );
        // No quantity selector for invoice line or for courseId
        if (
            !props.data.cartLine.IsInvoiceLine &&
            !courseId // hide for courses
        ) {
            if (props.data.product && props.isQuantityEditable) {
                nodes.push(
                    <div className='msc-cart-line__product-quantity'>
                        <div className='msc-cart-line__product-quantity-label'>{resources.quantityDisplayString}</div>

                        {generateErrorMessage()}

                        <IncrementalQuantity
                            id={`msc-cart-line__quantity_${props.data.product.RecordId}-
                            ${String(props.data.cartLine.DeliveryMode)}-${String(props.data.cartLine.LineId)}`}
                            max={props.maxQuantity ?? defaultMaxQuantity}
                            currentCount={props.currentQuantity}
                            onChange={onChange}
                            inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                            decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
                            incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
                            key={props.data.cartLine.LineId}
                            disabled={!props.isCartStateReady}
                            isGiftCard={props.data.product.IsGiftCard ?? props.isOutOfStock}
                            telemetryContent={props.telemetryContent}
                        />
                        {unitOfMeasure}
                    </div>
                );
            } else {
                nodes.push(
                    <div
                        className={classNames('msc-cart-line__quantity', {
                            'single-quantity': props.data.cartLine.Quantity === singleQuantity
                        })}
                    >
                        {unitOfMeasure}
                        <label className='quantity-label'>
                            {resources.quantityDisplayString}
                            {': '}
                        </label>

                        {generateErrorMessage()}

                        <span className='quantity-value'>{props.data.cartLine.Quantity}</span>
                    </div>
                );
            }
        }

        nodes.push(
            <div className='msc-cart-line__product-savings'>
                <PriceComponent
                    data={
                        isSalesLine
                            ? {
                                  price: {
                                      // @ts-expect-error -- Auto suppressed.
                                      CustomerContextualPrice: props.data.cartLine.NetAmount,
                                      BasePrice: props.data.cartLine.Price
                                  }
                              }
                            : {
                                  price: {
                                      CustomerContextualPrice: props.data.cartLine.NetAmountWithoutTax,
                                      BasePrice: props.data.cartLine.NetPrice
                                  }
                              }
                    }
                    context={props.context}
                    id={props.id}
                    typeName={props.typeName}
                    className='msc-cart-line__product-savings-actual'
                    originalPriceText={resources.originalPriceText}
                    currentPriceText={resources.currentPriceText}
                />
                {props.isOutOfStock ? renderInventoryLabel : null}
            </div>
        );

        return nodes;
    };

    return (
        <>
            <div className='msc-cart-line'>
                <a className={imgeClassName} href={productUrl} aria-label={productName}>
                    {(() => {
                        const requestContext = props.context.actionContext.requestContext;
                        const itemId = product?.ItemId || props.data.cartLine.ItemId;
                        const manufacturerCode = getManufacturerCode(itemId);

                        let manufacturerFallback: string | undefined;
                        if (requestContext && manufacturerCode) {
                            manufacturerFallback = getFallbackImageSrc(requestContext, manufacturerCode);
                        }

                        const effectiveFallBack = manufacturerFallback || fallbackImageUrl;
                        return (
                            <Image
                                requestContext={requestContext}
                                aria-hidden='true'
                                src={props.primaryImageUrl ?? 'empty'}
                                fallBackSrc={effectiveFallBack}
                                altText={productName ?? 'Product'}
                                gridSettings={props.gridSettings}
                                imageSettings={imageSettings}
                                loadFailureBehavior='empty'
                                className={imgeClassName}
                            />
                        );
                    })()}
                </a>
                <div className={cartLineContentClassName}>
                    <div className='msc-cart-line__product'>
                        <a className='msc-cart-line__product-title' href={productUrl} {...productAttribute}>
                            {product?.ItemId || null}
                            {!IsInvoiceLine && '-'}
                            {productName}
                        </a>
                        {/* <div className='msc-cart-line__product__invoice-terms'>
                        <span>Terms</span>
                        <span>Allowable Freight by 15th</span>
                    </div> */}
                        {ArrayExtensions.hasElements(productDimensions) ? (
                            <div className='msc-cart-line__product-variants'>{productDimensions}</div>
                        ) : (
                            ''
                        )}
                        <div className='msc-cart-line__product-price'>
                            <PriceComponent
                                data={
                                    isSalesLine
                                        ? {
                                              price: {
                                                  // @ts-expect-error -- Auto suppressed.
                                                  CustomerContextualPrice: props.data.cartLine.NetAmount,
                                                  BasePrice: props.data.cartLine.Price
                                              }
                                          }
                                        : {
                                              price: {
                                                  CustomerContextualPrice: props.data.cartLine.NetAmountWithoutTax,
                                                  BasePrice: props.data.cartLine.NetPrice
                                              }
                                          }
                                }
                                context={props.context}
                                id={props.id}
                                typeName={props.typeName}
                                className='msc-cart-line__product-discount-value'
                            />
                            {renderUnitOfMeasure()}
                        </div>
                        {renderProductUnitPrice}
                        {renderInventoryLabel}
                        {renderDiscountLines}
                        {props.showShippingChargesForLineItems && <div className='msc-cart-line__freight'>{renderShippingLabel}</div>}
                        {renderOtherCharges}
                    </div>
                    {generateQuantityAndPrice()}
                    {props.data.cartLine.IsInvoiceLine && (
                        <div className='msc-cart-line__invoice'>
                            <p className='invoice-amount-label'>Payment Amount</p>
                            <p className='invoice-price'>
                                {props.data.cartLine.Price
                                    ? props.context.cultureFormatter.formatCurrency(props.data.cartLine.Price)
                                    : 'N/A'}
                            </p>
                        </div>
                    )}
                    {supersessionPreview && supersessionPreview.Allocations.length > 0 && props.isCartView && (
                        <SupersessionBreakdownComponent supersessionPreview={supersessionPreview} props={props} />
                    )}
                </div>
            </div>

            {/* render the Prop 65 warning message */}
            <div className='msc-cart-line_prop-65'>{renderProp65Warning()}</div>
        </>
    );
};

export default CartLine;
